import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IonButton, IonContent, IonPage, IonSpinner } from "@ionic/react";
import { FitnessDataType, GpsFilteredData, HalfEvent, HrData, MatchPhone } from "refsix-js-models";
import { getFitnessData } from "../../services/fitnessDataSync/fitnessDataSyncService";
import { useProfile, useUsername } from "../../components/hooks";
import { useTranslation } from "react-i18next";
import { calculateCompositeIntensity } from "../../utils/intensityCalculation";
import { saveMatchTimings, skipMatchTimings } from "../../services/matchTimingsService";
import { trackEvent } from "../../services/analytics/analyticsService";
import MatchTimingsGraph from "./MatchTimingsGraph";
import HeaderComponent from "../../components/HeaderComponent";
import "./matchTimings.css";

interface MatchTimingsProps {
    match: MatchPhone;
    onComplete: () => void;
}

const DEFAULT_SLIDER_POSITIONS = [0.2, 0.4, 0.6, 0.8];
const ANIMATION_START_POSITIONS = [0.01, 0.02, 0.03, 0.04];
const ANIMATION_DURATION_MS = 1000;
const STAGGER_MS = 200;
const MAX_DISPLAY_DURATION_MS = 3 * 60 * 60 * 1000;

function easeOut(t: number): number {
    return 1 - (1 - t) * (1 - t);
}

const MatchTimings: FC<MatchTimingsProps> = ({ match, onComplete }) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const username = useUsername();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gpsData, setGpsData] = useState<GpsFilteredData | undefined>();
    const [hrData, setHrData] = useState<HrData | undefined>();
    const [sliderPositions, setSliderPositions] = useState<number[]>(ANIMATION_START_POSITIONS);
    const animatingRef = useRef(true);

    // Load raw fitness data directly (bypassing FitnessDataProvider)
    useEffect(() => {
        const loadData = async () => {
            if (!match._id || !username) return;

            setLoading(true);
            try {
                if (match.hasTracking) {
                    const rawGps = await getFitnessData(
                        match._id,
                        username,
                        FitnessDataType.GPS
                    );
                    setGpsData(rawGps as GpsFilteredData);
                }
            } catch (e) {
                console.log("Error loading GPS data:", e);
            }

            try {
                if (match.hasHeartRate) {
                    const rawHr = await getFitnessData(
                        match._id,
                        username,
                        FitnessDataType.HeartRate
                    );
                    setHrData(rawHr as HrData);
                }
            } catch (e) {
                console.log("Error loading HR data:", e);
            }

            setLoading(false);
        };

        loadData();
    }, [match._id, username, match.hasTracking, match.hasHeartRate]);

    // Animate sliders from top to their default positions on mount
    useEffect(() => {
        if (loading) return;

        let startTime: number | null = null;
        let rafId: number;

        const totalDuration = ANIMATION_DURATION_MS + STAGGER_MS * (DEFAULT_SLIDER_POSITIONS.length - 1);

        const animate = (timestamp: number) => {
            if (startTime === null) startTime = timestamp;
            const elapsed = timestamp - startTime;

            const interpolated = ANIMATION_START_POSITIONS.map((start, i) => {
                const sliderDuration = ANIMATION_DURATION_MS + i * STAGGER_MS;
                const t = Math.min(elapsed / sliderDuration, 1);
                const eased = easeOut(t);
                return start + (DEFAULT_SLIDER_POSITIONS[i] - start) * eased;
            });

            setSliderPositions(interpolated);

            if (elapsed < totalDuration) {
                rafId = requestAnimationFrame(animate);
            } else {
                animatingRef.current = false;
            }
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [loading]);

    // Determine activity time boundaries from raw GPS/HR data
    const { activityStartTime, activityEndTime } = useMemo(() => {
        let start = 0;
        let end = 0;

        if (gpsData?.geoPoints && gpsData.geoPoints.length > 0) {
            start = gpsData.geoPoints[0].time || 0;
            end = gpsData.geoPoints[gpsData.geoPoints.length - 1].time || 0;
        }

        if (hrData?.heartRateValues && hrData.heartRateValues.length > 0) {
            const hrStart = hrData.heartRateValues[0].time || 0;
            const hrEnd = hrData.heartRateValues[hrData.heartRateValues.length - 1].time || 0;
            if (start === 0 || hrStart < start) start = hrStart;
            if (hrEnd > end) end = hrEnd;
        }

        if (end - start > MAX_DISPLAY_DURATION_MS) {
            end = start + MAX_DISPLAY_DURATION_MS;
        }

        return { activityStartTime: start, activityEndTime: end };
    }, [gpsData, hrData]);

    // Calculate max HR from profile
    const maxHR = useMemo(() => {
        if (profile?.dob && match?.date) {
            const birthDate = new Date(profile.dob);
            const matchDate = new Date(match.date);
            const age = matchDate.getFullYear() - birthDate.getFullYear();
            return 220 - age;
        }
        return 190;
    }, [profile?.dob, match?.date]);

    // Calculate intensity data using synthetic timings
    const intensityData = useMemo(() => {
        if (!gpsData?.geoPoints || gpsData.geoPoints.length === 0) return [];

        // Create synthetic single-element timing to span the entire activity
        const syntheticTimings: HalfEvent[] = [
            { timestamp: activityStartTime, endTime: activityEndTime } as HalfEvent,
        ];

        return calculateCompositeIntensity(gpsData, hrData, syntheticTimings, maxHR);
    }, [gpsData, hrData, activityStartTime, activityEndTime, maxHR]);

    // Constrained slider change handler (disabled during entrance animation)
    const handleSliderChange = useCallback(
        (index: number, newPosition: number) => {
            if (animatingRef.current) return;
            setSliderPositions((prev) => {
                const updated = [...prev];
                const minGap = 0.01;

                // Determine bounds
                const minVal = index === 0 ? minGap : prev[index - 1] + minGap;
                const maxVal = index === prev.length - 1 ? 1 - minGap : prev[index + 1] - minGap;

                updated[index] = Math.max(minVal, Math.min(maxVal, newPosition));
                return updated;
            });
        },
        []
    );

    const handleSkip = useCallback(async () => {
        if (!profile || saving) return;

        setSaving(true);
        try {
            await skipMatchTimings(match, profile);
            trackEvent("PlayingPeriodsSkipped", {
                matchId: match._id,
                hasTracking: match.hasTracking,
                hasHeartRate: match.hasHeartRate,
            });
            onComplete();
        } catch (e) {
            console.error("Error skipping match timings:", e);
        } finally {
            setSaving(false);
        }
    }, [match, profile, saving, onComplete]);

    const handleSave = useCallback(async () => {
        if (!profile || saving) return;

        setSaving(true);
        try {
            const periodLengths = await saveMatchTimings(
                match,
                sliderPositions,
                activityStartTime,
                activityEndTime,
                profile
            );
            trackEvent("PlayingPeriodsSaved", {
                matchId: match._id,
                hasTracking: match.hasTracking,
                hasHeartRate: match.hasHeartRate,
                ...periodLengths,
            });
            onComplete();
        } catch (e) {
            console.error("Error saving match timings:", e);
        } finally {
            setSaving(false);
        }
    }, [match, sliderPositions, activityStartTime, activityEndTime, profile, saving, onComplete]);

    if (loading) {
        return (
            <div className="match-timings-content">
                <div className="match-timings-loading">
                    <IonSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className="match-timings-content">
            <p className="match-timings-description">
                {t("matchTimings.matchTimingsDescription")}
            </p>

            <MatchTimingsGraph
                intensityData={intensityData}
                sliderPositions={sliderPositions}
                onSliderChange={handleSliderChange}
                activityStartTime={activityStartTime}
                activityEndTime={activityEndTime}
            />

            <div className="match-timings-save-wrapper">
                <IonButton
                    className="match-timings-skip-btn"
                    fill="outline"
                    onClick={handleSkip}
                    disabled={saving}
                >
                    {t("general.skip")}
                </IonButton>
                <IonButton
                    className="match-timings-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? <IonSpinner name="crescent" /> : t("general.save")}
                </IonButton>
            </div>
        </div>
    );
};

export default MatchTimings;
